import { api } from './client';

// ── Types ──

export interface AnalysisStepStatus {
  step_number: number;
  step_name: string;
  status: string;
}

export interface StepDetail {
  step: number;
  step_name: string;
  status: string;
  ai_model?: string | null;
  duration_ms?: number | null;
  tokens_input?: number | null;
  tokens_output?: number | null;
  cost?: number | null;
  input_summary?: string | null;
  output_markdown?: string | null;
  output_json?: Record<string, unknown> | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  serp_results_count?: number;
  ngram_url_count?: number;
}

export interface StepsResponse {
  request_id: string;
  steps: StepDetail[];
}

export interface AnalysisReport {
  step: number;
  step_name: string;
  status: string;
  report: string | null;
  report_json: Record<string, unknown> | null;
  ai_model: string | null;
  tokens_input: number | null;
  tokens_output: number | null;
  cost: number | null;
  duration_ms: number | null;
  error_message: string | null;
  system_prompt: string | null;
  user_prompt: string | null;
  prompt_id: number | null;
  prompt_version: number | null;
}

export interface NgramItem {
  task_id: string;
  url: string | null;
  domain: string | null;
  heading_count: number;
  ngrams_2: Record<string, unknown>[] | null;
  ngrams_3: Record<string, unknown>[] | null;
  ngrams_4: Record<string, unknown>[] | null;
}

export interface AnalysisListItem {
  request_id: string;
  keyword: string;
  status: string;
  steps_total: number;
  steps_completed: number;
  total_cost: number;
  created_at: string;
  completed_at: string | null;
  parsed_htags: number;
  analysis_ready: boolean;
}

export interface AnalysisListResponse {
  items: AnalysisListItem[];
  total: number;
  stats: {
    total: number;
    completed: number;
    running: number;
    failed: number;
    total_cost: number;
  };
}

// H-Tags types
export interface HeadingItem {
  level: number;
  text: string;
}

export interface HTagsCompetitor {
  rank: number;
  domain: string;
  url: string;
  title: string;
  headings: HeadingItem[];
}

export interface HTagsTopic {
  normalized: string;
  representative: string;
  variants: string[];
  level: number;
  site_count: number;
  percentage: number;
  domains: string[];
}

export interface HTagsResponse {
  request_id: string;
  total_sites: number;
  competitors: HTagsCompetitor[];
  topics: HTagsTopic[];
}

// AI-powered H-tag topic grouping
export interface HTagsAiTopic {
  topic: string;
  site_count: number;
  percentage: number;
  example_headings: string[];
  suggested_h_level: number;
}

export interface HTagsAiTopicsResponse {
  request_id: string;
  topics: HTagsAiTopic[];
  cached: boolean;
  ai_model: string;
  cost: number;
}

// Nav-Analysis types
export interface NavTreeNode {
  label: string;
  url?: string;
  children?: NavTreeNode[];
}

export interface NavAnalysisSite {
  rank: number;
  domain: string;
  url: string;
  nav_count: number;
  nav_source: string;
  waf_blocked: boolean;
  nav_tree: NavTreeNode[];
}

export interface NavCommonItem {
  label: string;
  count: number;
  percentage: number;
  domains: string[];
}

export interface NavAnalysisResponse {
  request_id: string;
  total_sites: number;
  sites_with_nav: number;
  waf_blocked: number;
  common_nav: NavCommonItem[];
  sites: NavAnalysisSite[];
}

// PAA (People Also Ask) types
export interface PaaItem {
  title: string | null;
  data_json: Record<string, unknown> | null;
  rank_absolute: number | null;
}

// SERP Results types (DataForSEO raw data)
export interface SerpOrganicResult {
  rank_absolute: number;
  rank_group: number;
  item_type: string;
  domain: string | null;
  title: string | null;
  url: string | null;
  snippet: string | null;
  extra_data_json: Record<string, unknown> | null;
}

export interface SerpFeature {
  feature_type: string;
  title: string | null;
  data_json: Record<string, unknown> | null;
  rank_absolute: number | null;
}

export interface SerpResultsResponse {
  organic: SerpOrganicResult[];
  features: SerpFeature[];
}

// Rank Tracking types
export interface RankSnapshotPoint {
  date: string;
  rank: number;
}

export interface RankTrendItem {
  url: string;
  domain: string;
  snapshots: RankSnapshotPoint[];
}

export interface RankChange {
  url: string;
  domain: string;
  old_rank: number | null;
  new_rank: number | null;
  diff: number;
  status: "up" | "down" | "new" | "dropped" | "unchanged";
}

export interface RankChangesResponse {
  current_date: string;
  previous_date: string;
  changes: RankChange[];
}

// Keyword pool types
export interface KwPoolItem {
  keyword: string;
  translation: string | null;
  monthly_searches: number | null;
  cat1: string | null;
  cat2: string | null;
  cat3: string | null;
  search_intent: string | null;
  buying_stage: string | null;
}

export interface KwPoolResponse {
  success: boolean;
  data: KwPoolItem[];
  total: number;
}

// SEO Details types
export interface SeoCompetitor {
  url: string;
  domain: string;
  title: string | null;
  meta_description: string | null;
  h1_count: number;
  h2_count: number;
  h3_count: number;
  word_count: number;
  images_count: number;
  images_json: unknown;
  indexable: boolean | null;
  has_schema_markup: boolean;
  overall_score: number | null;
  title_score: number | null;
  description_score: number | null;
  heading_score: number | null;
  url_score: number | null;
  content_score: number | null;
  links_score: number | null;
  images_score: number | null;
  security_score: number | null;
  indexing_score: number | null;
  issues_json: unknown;
  keyword_in_title: boolean | null;
  keyword_in_description: boolean | null;
}

export interface SeoDataResponse {
  request_id: string;
  target_keyword: string;
  competitors: SeoCompetitor[];
}

// ── API ──

export const serpAnalysisApi = {
  /** List all analysis requests with status summary */
  list: (filters: { limit?: number; offset?: number; project_id?: string } = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.set('limit', String(filters.limit));
    if (filters.offset) params.set('offset', String(filters.offset));
    if (filters.project_id) params.set('project_id', filters.project_id);
    const qs = params.toString();
    return api.get<AnalysisListResponse>(
      `/serp-analysis/list${qs ? `?${qs}` : ''}`,
    );
  },

  /** Trigger SERP analysis for a request */
  trigger: (requestId: string, keyword: string) =>
    api.post<{ status: string; request_id: string }>(
      '/serp-analysis/trigger',
      { request_id: requestId, keyword },
    ),

  /** Queue scraping tasks for TOP20 pages missing h_structure_json */
  fetchCompetitors: (requestId: string) =>
    api.post<{ success: boolean; data: { queued: number; total_missing: number } }>(
      `/serp-analysis/fetch-competitors/${requestId}`,
      {},
    ),

  /** Get analysis status per step */
  getStatus: (requestId: string) =>
    api.get<{ steps: AnalysisStepStatus[] }>(
      `/serp-analysis/status/${requestId}`,
    ),

  /** Get full step details (16 fields, Step1+2 synthesized from serp_requests + ngram_analysis) */
  getSteps: (requestId: string) =>
    api.get<StepsResponse>(`/serp-analysis/steps/${requestId}`),

  /** Get full analysis results (all step reports) */
  getResults: (requestId: string) =>
    api.get<{
      request_id: string;
      keyword: string;
      reports: AnalysisReport[];
      total_cost: number;
    }>(`/serp-analysis/results/${requestId}`),

  /** Get ngram analysis results */
  getNgrams: (requestId: string) =>
    api.get<{
      request_id: string;
      items: NgramItem[];
    }>(`/serp-analysis/ngrams/${requestId}`),

  /** Get H-tag structures for top 20 competitors */
  getHtags: (requestId: string) =>
    api.get<HTagsResponse>(`/serp-analysis/htags/${requestId}`),
  /** Download all persisted H1-H6 rows for this request as Excel */
  exportHtagsExcel: (requestId: string) =>
    api.download(`/serp-analysis/htags/${requestId}/export`),

  /** Get navigation analysis for top 30 competitors */
  getNavAnalysis: (requestId: string) =>
    api.get<NavAnalysisResponse>(`/serp-analysis/nav-analysis/${requestId}`),

  /** Get AI-powered H-tag topic grouping (cached) */
  getHtagsTopics: (requestId: string) =>
    api.get<HTagsAiTopicsResponse>(`/serp-analysis/htags-topics/${requestId}`),

  /** Get SEO details (title, description, H-tags, images, scores) for all competitors */
  getSeoData: (requestId: string) =>
    api.get<SeoDataResponse>(`/serp-analysis/seo-data/${requestId}`),

  /** Get People Also Ask questions from serp_features */
  getPaa: (requestId: string) =>
    api.get<{ success: boolean; data: PaaItem[] }>(
      `/serp-analysis/paa/${requestId}`,
    ),

  /** Get keyword pool from APP16 (source=app05) */
  getKwPool: (filters: { search?: string; cat1?: string; cat2?: string; page?: number; per_page?: number } = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.cat1) params.set('cat1', filters.cat1);
    if (filters.cat2) params.set('cat2', filters.cat2);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.per_page) params.set('per_page', String(filters.per_page));
    const qs = params.toString();
    return api.get<KwPoolResponse>(
      `/serp-analysis/kw-pool${qs ? `?${qs}` : ''}`,
    );
  },

  /** Get Alibaba merged product data for a keyword */
  getAlibabaData: (keyword: string, limit = 500) =>
    api.get<{
      success: boolean;
      data: {
        summary: {
          totalProducts: number;
          totalCompanies: number;
          avgPrice: number;
          attrCount: number;
          levelA: number;
          levelAB: number;
          parsedOk: number;
          parsedPartial: number;
          parsedFailed: number;
          hasImages: number;
        };
        attrColumns: Array<{ key: string; coverage: number; total: number; pct: number }>;
        rows: Array<Record<string, unknown>>;
      };
      meta: { total: number; limit: number; offset: number };
    }>(`/alibaba-data-viewer?keyword=${encodeURIComponent(keyword)}&limit=${limit}`),

  /** Get raw DataForSEO SERP results (organic + feature blocks) */
  getSerpResults: (requestId: string) =>
    api.get<SerpResultsResponse>(`/serp-analysis/serp-results/${requestId}`),

  /** Get rank trend (per-domain history) for a keyword */
  getRankTrends: (keyword: string, topN = 10, locationCode = 2840) =>
    api.get<RankTrendItem[]>(
      `/rank-tracking/trends/${encodeURIComponent(keyword)}?top_n=${topN}&location_code=${locationCode}`
    ),

  /** Get rank changes (latest two snapshots comparison) */
  getRankChanges: (keyword: string, locationCode = 2840) =>
    api.get<RankChangesResponse>(
      `/rank-tracking/changes/${encodeURIComponent(keyword)}?location_code=${locationCode}`
    ),

  /** Re-fetch DataForSEO SERP data (clears old, calls API again).
   *  Returns skipped:true when same keyword+location already fetched today (dedup guard). */
  refreshSerpResults: (requestId: string) =>
    api.post<{ request_id: string; keyword: string; organic_count?: number; message: string; skipped: boolean; existing_request_id?: string }>(
      `/serp-analysis/refresh-serp/${requestId}`,
      {}
    ),

  /** Force recrawl: re-fetch SERP + clear failed tasks + re-queue non-blocked pages */
  forceRecrawl: (requestId: string) =>
    api.post<{
      success: boolean;
      data: {
        request_id: string;
        keyword: string;
        serp_refreshed: boolean;
        failed_tasks_deleted: number;
        queued: number;
        blocked_skipped: number;
        message: string;
      };
    }>(`/serp-analysis/force-recrawl/${requestId}`, {}),

  /** Bulk export completed analysis keywords for a project (40-column Excel or JSON) */
  bulkExport: (projectId: string, options: {
    format?: 'excel' | 'json';
    requireAllSteps?: boolean;
  } = {}) => {
    const params = new URLSearchParams({ project_id: projectId });
    if (options.format) params.append('format', options.format);
    if (options.requireAllSteps === false) params.append('require_all_steps', 'false');
    return api.download(`/serp-analysis/bulk-export?${params.toString()}`);
  },
};
